import { useMemo } from 'react';

interface UseHeaderVisibilityProps {
  currentPath: string;
}

interface UseHeaderVisibilityReturn {
  isTopHeaderEnabled: boolean;
  shouldShowBreadcrumbs: boolean;
}

// Route pattern constants
const ROUTE_PATTERNS = {
  PAGE_INDICATOR: 'page',
  DETAILS_INDICATOR: 'details',
} as const;

// Route depth threshold for header visibility
const MAX_ROUTE_DEPTH_FOR_ENABLED_HEADER = 1;

// Paths where the top header is always enabled, even when the route contains 'page'
const HEADER_FORCE_ENABLED_PATHS = [
  'subscriptions/orders',
  'subscriptions/products',
  'affiliates/program',
  'affiliates/products',
  'affiliates/settings'
] as const;

// Paths excluded from showing breadcrumbs
const BREADCRUMB_EXCLUDED_PATHS = [
  'product-details',
  'customer-details',
  'document',
  'order-details',
  'split-order-details',
  'company-details',
  'brand-details',
  'service-details',
  'organizations',
  'subscription-details'
] as const;

/**
 * Custom hook for managing header visibility logic
 * 
 * Determines when to show/hide various header elements based on:
 * - Current route depth
 * - Specific path patterns
 * - Page type indicators
 * 
 * @param props - Configuration object with current path
 * @returns Visibility flags for header elements
 */
export const useHeaderVisibility = ({
  currentPath
}: UseHeaderVisibilityProps): UseHeaderVisibilityReturn => {

  // Determine if top header should be enabled based on route depth or 'page' indicator
  const isTopHeaderEnabled = useMemo(() => {

    const isForceEnabled = HEADER_FORCE_ENABLED_PATHS.some(p => currentPath.includes(p)) && !currentPath.includes('details');
    if (isForceEnabled) return true;
    
    const routeDepth = (currentPath.match(/\//g) || []).length;
    return routeDepth <= MAX_ROUTE_DEPTH_FOR_ENABLED_HEADER || currentPath.includes(ROUTE_PATTERNS.PAGE_INDICATOR);

  }, [currentPath]);

  // Determine if breadcrumbs should be shown
  const shouldShowBreadcrumbs = useMemo(() => {
    const hasDetailsInPath = currentPath.includes(ROUTE_PATTERNS.DETAILS_INDICATOR);
    const isExcludedPath = BREADCRUMB_EXCLUDED_PATHS.some(excludedPath =>
      currentPath.includes(excludedPath)
    );

    return hasDetailsInPath && !isExcludedPath;
  }, [currentPath]);

  return {
    isTopHeaderEnabled,
    shouldShowBreadcrumbs
  };
};